import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PermissionGate } from './PermissionGate';
import styles from './ContextActionsMenu.module.scss';

type ContextActionsMenuProps = {
  readonly x: number;
  readonly y: number;
  readonly onClose: () => void;
  readonly onOpen: () => void;
  readonly onChat: () => void;
  readonly onDelete: () => void;
};

/**
 * Right-click context menu (Open / Chat / Delete) shared by the client table
 * rows and the funnel board cards. Position is clamped to the viewport and the
 * menu closes on outside click, scroll, resize, or Escape.
 */
export function ContextActionsMenu({ x, y, onClose, onOpen, onChat, onDelete }: ContextActionsMenuProps) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x, top: y });

  useEffect(() => {
    const close = () => onClose();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    if (!menuRef.current) {
      return;
    }
    const rect = menuRef.current.getBoundingClientRect();
    const maxLeft = window.innerWidth - rect.width - 8;
    const maxTop = window.innerHeight - rect.height - 8;
    setPosition({
      left: Math.max(8, Math.min(x, maxLeft)),
      top: Math.max(8, Math.min(y, maxTop)),
    });
  }, [x, y]);

  function runAction(action: () => void) {
    onClose();
    action();
  }

  return (
    <div
      ref={menuRef}
      className={styles.menu}
      role="menu"
      style={{ left: position.left, top: position.top }}
      onMouseDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <button
        type="button"
        role="menuitem"
        className={styles.item}
        onClick={() => runAction(onOpen)}
      >
        {t('clients.open')}
      </button>
      <button
        type="button"
        role="menuitem"
        className={styles.item}
        onClick={() => runAction(onChat)}
      >
        {t('clients.chat')}
      </button>
      <PermissionGate permission="patients:delete">
        <button
          type="button"
          role="menuitem"
          className={`${styles.item} ${styles.itemDanger}`}
          onClick={() => runAction(onDelete)}
        >
          {t('clients.delete')}
        </button>
      </PermissionGate>
    </div>
  );
}
